import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { nyDateKey } from '../../lib/datetime';
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import { GripVertical, CalendarDays } from 'lucide-react';
import { api } from '../../lib/api';
import { validTransitions, stageMeta, PIPELINE_COLUMNS, NO_AGING_STAGES, MEETING_COLUMN, boardColumnOf } from '../../lib/stages';
import Modal from '../../components/admin/Modal';
import StageTransitionDialog from '../../components/admin/StageTransitionDialog';
import { agingTone, AGING_BORDER_CLASS } from '../../lib/aging';

// Canonical 4-column subset from stages.js (post-019 cleanup).
// Cards fetched per column; the header shows the API's true total.
const PAGE_SIZE = 200;
// Meeting scheduled is a virtual column: the Engaged schools with a meeting
// date. It sits after Engaged and is fetched as its own slice so both
// columns show true totals.
const ACTIVE_COLUMNS = PIPELINE_COLUMNS.flatMap((s) => (s === 'engaged' ? ['engaged', MEETING_COLUMN] : [s]));
const EXTENDED_COLUMNS = ['dormant', 'closed_lost'];
const COLUMN_QUERY = { engaged: 'lifecycle_stage=engaged&meeting_scheduled=0', [MEETING_COLUMN]: 'lifecycle_stage=engaged&meeting_scheduled=1' };
const columnQuery = (column) => COLUMN_QUERY[column] || `lifecycle_stage=${encodeURIComponent(column)}`;
const todayKey = () => nyDateKey(new Date().toISOString());

export default function Pipeline() {
  const [accounts, setAccounts] = useState([]);
  // True per-stage totals from the API. The board fetches at most PAGE_SIZE
  // cards per column, so the header must not be the fetched array length.
  const [stageTotals, setStageTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showExtended, setShowExtended] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);
  const [pendingTransition, setPendingTransition] = useState(null); // { account, defaultTarget, forceMode, origStage, thenMeeting? }
  const [pendingMeeting, setPendingMeeting] = useState(null); // { account, date } — the date prompt for the Meeting scheduled column
  const [toast, setToast] = useState(null); // { message, detail? }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const stages = showExtended ? [...ACTIVE_COLUMNS, ...EXTENDED_COLUMNS] : ACTIVE_COLUMNS;
      const all = await Promise.all(stages.map(async (stage) => {
        const res = await api.get(`/api/admin/accounts?${columnQuery(stage)}&limit=${PAGE_SIZE}`);
        const cards = res.accounts || [];
        return { stage, cards, total: res.pagination?.total ?? cards.length };
      }));
      setAccounts(all.flatMap((r) => r.cards));
      setStageTotals(Object.fromEntries(all.map((r) => [r.stage, r.total])));
    } catch (err) {
      setError(err.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, [showExtended]);

  useEffect(() => { load(); }, [load]);

  const byStage = useMemo(() => {
    const map = {};
    for (const s of [...ACTIVE_COLUMNS, ...EXTENDED_COLUMNS]) map[s] = [];
    for (const a of accounts) {
      const col = boardColumnOf(a);
      if (map[col]) map[col].push(a);
    }
    return map;
  }, [accounts]);

  const activeAccount = activeDragId ? accounts.find((a) => a.id === activeDragId) : null;

  const showToast = (message, detail = null) => {
    setToast({ message, detail });
    setTimeout(() => setToast(null), 5000);
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over) return;
    const accountId = active.id;
    const target = over.id;
    const account = accounts.find((a) => a.id === accountId);
    if (!account) return;
    const fromColumn = boardColumnOf(account);
    if (fromColumn === target) return;

    // Meeting scheduled is a date on an Engaged school, not a stage.
    if (target === MEETING_COLUMN) {
      if (account.lifecycleStage === 'engaged') { setPendingMeeting({ account, date: account.meetingScheduledAt || todayKey() }); return; }
      // Not engaged yet: the usual move to Engaged first, then the date (the
      // Engaged form's next-action date is used when it is filled in).
      setAccounts((prev) => prev.map((a) => (a.id === accountId ? { ...a, lifecycleStage: 'engaged' } : a)));
      setStageTotals((prev) => bumpTotals(prev, fromColumn, 'engaged'));
      const allowedToEngaged = validTransitions(account.lifecycleStage, account.type || 'school');
      setPendingTransition({ account, defaultTarget: 'engaged', forceMode: !allowedToEngaged.includes('engaged'), origStage: account.lifecycleStage, thenMeeting: true });
      return;
    }
    // Back from Meeting scheduled to plain Engaged: just clear the date.
    if (fromColumn === MEETING_COLUMN && target === 'engaged') { await saveMeetingDate(account, null); return; }
    if (account.lifecycleStage === target) return;

    // Optimistic — move the card locally so the drop animates cleanly.
    // The dialog confirms the actual write; on cancel we reload to revert.
    setAccounts((prev) => prev.map((a) => a.id === accountId ? { ...a, lifecycleStage: target } : a));
    // Keep the column totals honest with the move (they come from the API,
    // not from the fetched card arrays).
    setStageTotals((prev) => bumpTotals(prev, fromColumn, target));

    // If the drop target isn't in the allowed transition map for the current
    // stage, open the dialog in forceMode so the user gets the override-reason
    // flow directly instead of a blocking error.
    const allowed = validTransitions(account.lifecycleStage, account.type || 'school');
    const forceMode = !allowed.includes(target);
    setPendingTransition({
      account,
      defaultTarget: target,
      forceMode,
      origStage: account.lifecycleStage,
    });
  };

  // PATCH the meeting date (null clears). The server's stage endpoint clears
  // it on any stage change, so a school never carries a stale meeting.
  const saveMeetingDate = async (account, date) => {
    setAccounts((prev) => prev.map((a) => (a.id === account.id ? { ...a, lifecycleStage: 'engaged', meetingScheduledAt: date } : a)));
    setStageTotals((prev) => bumpTotals(prev, boardColumnOf(account), date ? MEETING_COLUMN : 'engaged'));
    try {
      const res = await api.patch(`/api/admin/accounts/${account.id}`, { meetingScheduledAt: date });
      if (res?.account) setAccounts((prev) => prev.map((a) => (a.id === res.account.id ? res.account : a)));
    } catch (err) {
      await load();
      showToast(date ? 'Couldn’t save the meeting date' : 'Couldn’t clear the meeting date', err?.message);
    }
  };

  const columns = showExtended ? [...ACTIVE_COLUMNS, ...EXTENDED_COLUMNS] : ACTIVE_COLUMNS;

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Pipeline</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Drag cards to advance stages. Contact-implying transitions prompt for a summary.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-[12px] text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={showExtended} onChange={(e) => setShowExtended(e.target.checked)} className="accent-[#B0FC38]" />
            Show dormant / lost / churned
          </label>
        </div>
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveDragId(e.active.id)}
        onDragEnd={onDragEnd}
        onDragCancel={() => setActiveDragId(null)}
      >
        <div className="flex gap-3 overflow-x-auto pb-4">
          {columns.map((stage) => (
            <Column
              key={stage}
              stage={stage}
              cards={byStage[stage] || []}
              total={stageTotals[stage]}
              activeAccount={activeAccount}
              loading={loading}
            />
          ))}
        </div>
        <DragOverlay>
          {activeAccount && <Card account={activeAccount} isDragOverlay />}
        </DragOverlay>
      </DndContext>

      {pendingTransition && (
        <StageTransitionDialog
          open
          account={pendingTransition.account}
          defaultTarget={pendingTransition.defaultTarget}
          forceMode={pendingTransition.forceMode}
          onClose={() => {
            // Cancel/dismiss — revert the optimistic move so the card snaps
            // back to its original column.
            load();
            setPendingTransition(null);
          }}
          onConfirm={async (apiPayload) => {
            try {
              const res = await api.post(
                `/api/admin/accounts/${pendingTransition.account.id}/stage`,
                { target: apiPayload.to_stage, ...apiPayload },
              );
              if (res?.account) {
                setAccounts((prev) => prev.map((a) => a.id === res.account.id ? res.account : a));
              }
              // The stage moved but the draft invoice could not be prepared —
              // say so rather than letting the operator assume it exists.
              if (res?.draft_invoice_error) showToast('Stage updated, but no draft invoice', res.draft_invoice_error);
              const wantsMeeting = pendingTransition.thenMeeting;
              setPendingTransition(null);
              if (wantsMeeting && res?.account) {
                const fromForm = apiPayload?.structured_payload?.next_action_date;
                if (fromForm) await saveMeetingDate(res.account, fromForm);
                else setPendingMeeting({ account: res.account, date: todayKey() });
              }
            } catch (err) {
              // Snap-back + surface the error via toast; the dialog also
              // shows the message inline via its own serverError state.
              await load();
              showToast(
                err?.code === 'invalid_stage_transition'
                  ? `Can't move to ${stageMeta(apiPayload.to_stage).label}.`
                  : "Couldn't update stage. Try again?",
                err?.message,
              );
              throw err;
            }
          }}
        />
      )}

      {pendingMeeting && (
        <MeetingDateDialog
          account={pendingMeeting.account}
          initialDate={pendingMeeting.date}
          onClose={() => { setPendingMeeting(null); load(); }}
          onSave={async (date) => { const acct = pendingMeeting.account; setPendingMeeting(null); await saveMeetingDate(acct, date); }}
        />
      )}

      {toast && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-red-500/20 bg-[#0a0a0f]/95 backdrop-blur-md p-3 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.9)]">
          <div className="text-[13px] text-white">{toast.message}</div>
          {toast.detail && <div className="text-[11px] text-gray-500 mt-1">{toast.detail}</div>}
        </div>
      )}
    </div>
  );
}

// The date prompt behind the Meeting scheduled column.
function MeetingDateDialog({ account, initialDate, onClose, onSave }) {
  const [date, setDate] = useState(initialDate || '');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!date || saving) return;
    setSaving(true);
    try { await onSave(date); } finally { setSaving(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Meeting with ${account?.name || 'this school'}`} maxWidth="max-w-sm">
      <form onSubmit={submit} className="space-y-4" data-testid="meeting-date-dialog">
        <label className="block">
          <span className="block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide">Meeting date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required aria-label="Meeting date"
            className="w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30" />
        </label>
        <p className="text-[12px] text-gray-500">The school stays Engaged. It shows in the Meeting scheduled column until it moves on or the date is cleared (drag it back to Engaged).</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-[12px] text-gray-400 hover:text-white">Cancel</button>
          <button type="submit" disabled={!date || saving} className="px-4 py-2 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50" data-testid="meeting-date-save">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

// Move one unit of a stage total from `from` to `to` (no-op on unknown totals).
function bumpTotals(totals, from, to) {
  if (!totals || from === to) return totals;
  const next = { ...totals };
  if (Number.isFinite(next[from])) next[from] = Math.max(0, next[from] - 1);
  if (Number.isFinite(next[to])) next[to] = next[to] + 1;
  return next;
}

function Column({ stage, cards, total, activeAccount }) {
  const trueTotal = Number.isFinite(total) ? total : cards.length;
  const truncated = trueTotal > cards.length;
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const meta = stageMeta(stage);
  const navigate = useNavigate();

  // Hint valid-drop when dragging. The meeting column takes any school that is
  // Engaged or may become Engaged; plain Engaged also takes a meeting card back.
  const canDrop = activeAccount
    ? (stage === MEETING_COLUMN
      ? activeAccount.lifecycleStage === 'engaged' || validTransitions(activeAccount.lifecycleStage, activeAccount.type || 'school').includes('engaged')
      : (stage === 'engaged' && boardColumnOf(activeAccount) === MEETING_COLUMN) || validTransitions(activeAccount.lifecycleStage, activeAccount.type || 'school').includes(stage))
    : null;

  return (
    <div
      ref={setNodeRef}
      data-testid={`col-${stage}`}
      className={`shrink-0 w-72 rounded-2xl border ${meta.tone.split(' ').filter(c => c.startsWith('border-')).join(' ')} bg-white/[0.015] flex flex-col transition-colors ${isOver ? 'ring-1 ring-[#B0FC38]/40' : ''} ${canDrop === false ? 'opacity-60' : ''}`}
    >
      <div className={`px-3 py-2 rounded-t-2xl border-b border-white/[0.05] ${meta.tone}`}>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-wider">{meta.label}</div>
          <div className="text-[11px] opacity-70" data-testid={`col-count-${stage}`}>{trueTotal}</div>
        </div>
      </div>
      <div className="p-2 space-y-2 min-h-[120px]">
        {cards.length === 0 ? (
          <div className="text-[11px] text-gray-600 text-center py-4">No accounts</div>
        ) : (
          cards.map((a) => (
            <Card key={a.id} account={a} onOpen={() => navigate(`/admin/accounts/${a.id}`)} />
          ))
        )}
        {truncated && (
          <Link
            to={`/admin/accounts?${columnQuery(stage)}`}
            data-testid={`col-more-${stage}`}
            className="block text-center text-[11px] text-gray-500 hover:text-[#B0FC38] py-2"
          >
            Showing {cards.length} of {trueTotal} · view all
          </Link>
        )}
      </div>
    </div>
  );
}

function Card({ account, isDragOverlay = false, onOpen }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: account.id });
  // Stage-aging visual state — yellow border at >=14 days in stage, red at
  // >=30 days. Hardcoded thresholds for Sprint B; tunable per-stage thresholds
  // ship when Sprint D's migration 024 lands the pipeline_aging_config table.
  const tone = NO_AGING_STAGES.has(account?.lifecycleStage) ? 'normal' : agingTone(account?.lifecycleStageEnteredAt);
  const agingBorder = AGING_BORDER_CLASS[tone];
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      data-testid={`pipeline-card-${account.id}`}
      data-aging={tone}
      className={`rounded-xl border ${agingBorder} bg-[#0a0a0f]/80 p-3 ${isDragging ? 'opacity-30' : ''} ${isDragOverlay ? 'shadow-[0_10px_40px_-10px_rgba(176,252,56,0.4)] rotate-1' : ''} cursor-pointer`}
      onClick={(e) => {
        if (!isDragOverlay) onOpen?.();
      }}
    >
      <div className="flex items-start gap-2">
        <button
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="mt-0.5 text-gray-600 hover:text-gray-400 cursor-grab active:cursor-grabbing"
          aria-label="Drag"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-white font-medium truncate">{account.name}</div>
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-500">
            {account.typeSchoolDbn && <span className="font-mono">{account.typeSchoolDbn}</span>}
            {account.typeSchoolDistrict && <span>D{account.typeSchoolDistrict}</span>}
            {account.lastContactedAt && <span className="ml-auto">· {nyDateKey(account.lastContactedAt)}</span>}
          </div>
          {account.lifecycleStage === 'engaged' && account.meetingScheduledAt && (
            <div className={`mt-1.5 inline-flex items-center gap-1 text-[10.5px] ${account.meetingScheduledAt < todayKey() ? 'text-amber-300' : 'text-violet-300'}`} data-testid="card-meeting">
              <CalendarDays className="w-3 h-3" aria-hidden="true" /> Meeting {account.meetingScheduledAt < todayKey() ? 'was ' : ''}{account.meetingScheduledAt}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
